import { type UpdateLabelGroupMutation } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { updateLabelGroupMutationDocument } from "./label-group.gql";
import { EVENT_LABEL_GROUP_UPDATED, trackLabelEvent } from "./tracking";

export type UpdatedLabelGroup = UpdateLabelGroupMutation["updateLabelGroup"];

export interface UpdateLabelGroupInput {
  id: string;
  title?: string;
  color?: string;
  allowMultiSelect?: boolean;
}

export interface UseUpdateLabelGroupProps {
  onCompleted?: (data: UpdatedLabelGroup) => void;
  onError?: (e: Error) => void;
}

export const useUpdateLabelGroup = ({ onCompleted, onError }: UseUpdateLabelGroupProps = {}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: UpdateLabelGroupInput) => {
      const client = createOperationClient("UpdateLabelGroup");
      const data = await client.request(updateLabelGroupMutationDocument, { input });

      return data.updateLabelGroup;
    },
    onSuccess: (labelGroup) => {
      // Invalidate the group detail and list to refetch with updated nested labelList
      queryClient.invalidateQueries({
        queryKey: queryKeys.labelGroups.detail(labelGroup.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.labelGroups.list(labelGroup.workspaceId),
      });

      // Invalidate all label caches since group color change propagates to labels
      queryClient.invalidateQueries({
        queryKey: queryKeys.labels.details(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.labels.list(labelGroup.workspaceId),
      });

      // Track the event
      trackLabelEvent(EVENT_LABEL_GROUP_UPDATED, {
        labelGroupId: labelGroup.id,
      });

      // Call the onCompleted callback
      if (onCompleted) {
        onCompleted(labelGroup);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    updateLabelGroup: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
